import { Chip, CircularProgress, ClickAwayListener, Divider, Grid } from '@material-ui/core';
import { Icons, Tooltip, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useMemo, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { Deficiency, deficiencyAction, Discipline } from '../../models/models';
import useDeficiencyWithVersionComments from '../../rxdb/deficiencyComments/useDeficiencyWithVersionComments';
import type { DeficiencyFilesDocument } from '../../rxdb/deficiencyFiles/deficiencyFiles.types';
import type { RepairFactor } from '../../rxdb/repairFactor/repairFactor.types';
import type { Urgency } from '../../rxdb/urgency/urgency.types';
import { isJestTest } from '../../test-helpers/isJestTest';
import { useFileStorage } from '../../utilities/useFileStorage';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { IMAGE_PATH, XML_HEADER } from '../../utilities/constants';
import {
  generateActionsList,
  getStatusForDeficiency,
  getTagForActiveDeficieny,
} from '../../utilities/utilityFunctions';
import DeleteDeficiency from '../DeleteDeficiency';
import StatusLabel from '../StatusLabel';
import useStyles from './CondensedView.styles';

interface Props {
  deficiency: Deficiency;
  setEditDeficiencyDialog: (val: boolean) => void;
  setEditFile: (val: Deficiency) => void;
  repairFactorListData: RepairFactor[];
  setOpenAuditLogsDialog: (flag: boolean) => void;
  setAuditLogsDeficiencyId: (id: number) => void;
  onCopy: (val: string) => void;
  isEditable: boolean;
  setInspectionIdForActiveDeficiency: (id: number) => void;
  isActiveDeficiency: boolean;
  urgencyListData: Urgency[];
  reloadCondensedView: boolean;
}

const CondensedView: React.FC<Props> = ({
  deficiency,
  setEditDeficiencyDialog,
  setEditFile,
  repairFactorListData,
  setOpenAuditLogsDialog,
  setAuditLogsDeficiencyId,
  onCopy,
  isEditable,
  setInspectionIdForActiveDeficiency,
  isActiveDeficiency,
  urgencyListData,
  reloadCondensedView,
}) => {
  const { SiteDiscipline, CurrentUserSelected } = useMst();
  const {
    name,
    componentFailureId,
    componentType,
    remedialAction,
    repairFactorId,
    commentObservation,
    isCompletedDuringInspection,
    componentAreaInfo,
    lowerElevation,
    upperElevation,
    pkDeficiencyUUID,
    deficiencyFiles,
    deficiencyActionedList,
    assetInspectionId,
    isReInspectedComponent,
    deficiencyComponentRemoval,
    inspectorUrgency,
    componentVersionUUID,
    componentVersionId,
  } = deficiency;
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const isShaft = SiteDiscipline.disciplineId === Discipline.Shaft;
  const { getAttachmentFromIndexDB, retryFileDownload } = useFileStorage();
  const online = useOnlineStatus();
  const [imageURL, setImageURL] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openForAssignBack, setOpenForAssignBack] = useState<boolean>(false);
  const [openForAlertInfo, setOpenForAlertInfo] = useState<boolean>(false);
  const [assignedBackComment, setAssignedBackComment] = useState<string>('');
  const [attentionFlag, setAttentionFlag] = useState<boolean>(false);
  const classes = useStyles({ imageURL });
  const { selectedDeficiencyComments, selectedActiveDeficiencyComments } =
    useDeficiencyWithVersionComments(
      Number(componentFailureId),
      componentVersionId,
      isActiveDeficiency,
      CurrentUserSelected.inspectionId,
    );
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const [actionsList, setActionsList] = useState<deficiencyAction[]>([]);
  const componentRemovalToolTip = `Component ID ${componentFailureId} to be reviewed by SC for change in 'Reason for Removal'`;

  const urgency = useMemo(() => {
    return urgencyListData?.find(item => item.sort === inspectorUrgency);
  }, [urgencyListData, inspectorUrgency]);

  const statusLabel = useMemo(
    () => getTagForActiveDeficieny(deficiencyActionedList, deficiencyComponentRemoval, deficiency),
    [deficiencyActionedList, deficiencyComponentRemoval, deficiency],
  );

  const getS3URL = async (fileData: DeficiencyFilesDocument) => {
    let blobFile: Blob = (await getAttachmentFromIndexDB(
      isActiveDeficiency
        ? fileData?.fileComponentVersionUUId || fileData.fileAimsUUID
        : fileData.fileAimsUUID,
    )) as Blob;
    if (online && !isJestTest() && (!blobFile || blobFile.type === XML_HEADER)) {
      setFileLoading(true);
      blobFile = await retryFileDownload(
        fileData.path,
        isActiveDeficiency
          ? fileData?.fileComponentVersionUUId || fileData.fileAimsUUID
          : fileData.fileAimsUUID,
        true,
        fileData.isActiveDeficiencyFile,
      );
      setFileLoading(false);
    }
    const url = blobFile ? URL.createObjectURL(blobFile) : '';
    setImageURL(url);
    return url;
  };

  const setFile = async () => {
    if (!open) {
      setEditFile(deficiency);
      setEditDeficiencyDialog(true);
    }
  };

  const handleTooltipClose = (
    e: React.MouseEvent<Document, MouseEvent> | React.ChangeEvent<unknown>,
  ) => {
    setOpen(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const copyDeficiency = (componentVersionUUIDCopy: string) => {
    onCopy(componentVersionUUIDCopy);
  };

  const handleTooltipOpen = (e: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
    e.stopPropagation();
  };

  const repairFactor = (id: number) => {
    for (let i = 0; i < repairFactorListData?.length; i++) {
      if (id === Number(repairFactorListData[i]?.id)) {
        return repairFactorListData[i]?.name;
      }
    }
  };

  const openAuditDialog = (e: { stopPropagation: () => void }) => {
    setAuditLogsDeficiencyId(Number(componentFailureId));
    setInspectionIdForActiveDeficiency(assetInspectionId);
    setOpenAuditLogsDialog(true);
    e.stopPropagation();
  };

  const handleTooltipCloseForAssignBack = (e: {
    stopPropagation: () => void;
    preventDefault: () => void;
  }) => {
    setOpenForAssignBack(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTooltipCloseForAlertInfo = (e: {
    stopPropagation: () => void;
    preventDefault: () => void;
  }) => {
    setOpenForAlertInfo(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTooltipOpenForAssignBack = (e: { stopPropagation: () => void }) => {
    setOpenForAssignBack(true);
    e.stopPropagation();
  };

  const handleTooltipOpenForAlertInfo = (e: { stopPropagation: () => void }) => {
    setOpenForAlertInfo(true);
    e.stopPropagation();
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted && deficiencyFiles.length > 0) {
      getS3URL(deficiencyFiles[0]);
    }
    return () => {
      isMounted = false;
    };
  }, [deficiencyFiles, reloadCondensedView, online]);

  useEffect(() => {
    if (!isActiveDeficiency) {
      if (selectedDeficiencyComments) {
        setAssignedBackComment(selectedDeficiencyComments[0]?.comment);
        setAttentionFlag(selectedDeficiencyComments[0]?.attentionFlag);
      }
    } else {
      if (selectedActiveDeficiencyComments) {
        setAssignedBackComment(selectedActiveDeficiencyComments[0]?.comment);
        setAttentionFlag(selectedActiveDeficiencyComments[0]?.attentionFlag);
      }
    }
  }, [selectedDeficiencyComments, selectedActiveDeficiencyComments]);

  useEffect(() => {
    if (deficiencyActionedList) {
      setActionsList(generateActionsList(deficiencyActionedList));
    }
  }, [deficiencyActionedList]);

  return (
    <>
      <Grid
        className={classes.condensedViewCard}
        key={componentVersionUUID}
        onClick={setFile}
        data-testid={`edit-def-${pkDeficiencyUUID}`}
      >
        <Grid container spacing={2}>
          {statusLabel && (
            <Chip
              label={statusLabel}
              size="medium"
              className={
                statusLabel.includes('Re-Inspected')
                  ? classes.reInspected
                  : classes.removedFromPlanning
              }
            />
          )}
          <Grid item container xs={12} alignItems="center" className={classes.condensedViewGrid}>
            <Grid
              item
              xs={deficiencyActionedList ? 5 : 6}
              className={classes.condensedViewInnerGrid}
            >
              <Typography variant="h5" display="inline">
                {`Id: ${componentFailureId === '0' ? 'TBD' : componentFailureId} - ${name || ''}`}
              </Typography>
              <span className={classes.greenDot} />
              <Typography display="inline" variant="h5" className={classes.areaPcsId}>
                {isShaft
                  ? upperElevation + ' - ' + lowerElevation
                  : componentAreaInfo[0]?.areaPcsId}
              </Typography>
              {assignedBackComment?.length ? (
                <ClickAwayListener
                  onClickAway={e => (openForAssignBack ? handleTooltipCloseForAssignBack(e) : '')}
                >
                  <Grid className="inlineGrid">
                    <Tooltip
                      PopperProps={{ disablePortal: true }}
                      onClose={e => (openForAssignBack ? handleTooltipCloseForAssignBack(e) : '')}
                      open={openForAssignBack}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={
                        <Typography className="tooltipTextColor">{assignedBackComment}</Typography>
                      }
                      className={classes.inlineBlock}
                      placement="right"
                      arrow
                    >
                      <Typography
                        data-testid="asgn-back-tooltip"
                        onClick={e => {
                          if (openForAssignBack) handleTooltipCloseForAssignBack(e);
                          else handleTooltipOpenForAssignBack(e);
                        }}
                      >
                        <Icons.MessageSquareFeather
                          color={attentionFlag ? 'error' : 'primary'}
                          className={classes.messageIcon}
                        />
                      </Typography>
                    </Tooltip>
                  </Grid>
                </ClickAwayListener>
              ) : null}

              {deficiencyActionedList &&
                isReInspectedComponent &&
                deficiencyComponentRemoval?.componentRemovalId &&
                !deficiencyComponentRemoval?.componentRemovalIdBySC && (
                  <ClickAwayListener
                    onClickAway={e => (openForAlertInfo ? handleTooltipCloseForAlertInfo(e) : '')}
                  >
                    <Grid className="inlineGrid">
                      <Tooltip
                        PopperProps={{ disablePortal: true }}
                        onClose={e => (openForAlertInfo ? handleTooltipCloseForAlertInfo(e) : '')}
                        open={openForAlertInfo}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={
                          <Typography className="tooltipTextColor">
                            {componentRemovalToolTip}
                          </Typography>
                        }
                        className={classes.inlineBlock}
                        placement="right"
                        arrow
                      >
                        <Typography
                          data-testid="asgn-back-tooltip"
                          onClick={e => {
                            if (openForAlertInfo) handleTooltipCloseForAlertInfo(e);
                            else handleTooltipOpenForAlertInfo(e);
                          }}
                        >
                          <Icons.AlertCircleIcon color="error" className={classes.alertCircle} />
                        </Typography>
                      </Tooltip>
                    </Grid>
                  </ClickAwayListener>
                )}

              {isElectrical && isCompletedDuringInspection && (
                <Typography color="primary">
                  Completed as part of Preventative Maintenance
                </Typography>
              )}

              {!isElectrical && (
                <Typography className={classes.condensedViewRisk}>
                  {urgency?.name || '-'}
                </Typography>
              )}

              {!isElectrical &&
                (commentObservation?.length > 30 ? (
                  <ClickAwayListener onClickAway={e => (open ? handleTooltipClose(e) : '')}>
                    <div>
                      <Typography
                        variant="body1"
                        className={`${classes.condensedViewDescription} ${classes.deficiencyDetailsCard}`}
                      >
                        {`${commentObservation?.substr(0, 30).replace(/\s*$/, '')}...`}
                      </Typography>
                      <Tooltip
                        data-testid="comment-observation-tooltip"
                        PopperProps={{
                          disablePortal: true,
                        }}
                        onClose={handleTooltipClose}
                        open={open}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={
                          <Typography className="tooltipTextColor">{commentObservation}</Typography>
                        }
                        className={classes.inlineBlock}
                        placement="bottom"
                        arrow
                      >
                        <Typography
                          color="primary"
                          className={classes.inlineBlock}
                          onClick={handleTooltipOpen}
                        >
                          Read more
                        </Typography>
                      </Tooltip>
                    </div>
                  </ClickAwayListener>
                ) : (
                  <Typography variant="body1" className={classes.condensedViewDescription}>
                    {commentObservation?.length > 0 ? commentObservation : ''}
                  </Typography>
                ))}
            </Grid>

            <Grid item xs={3}>
              {!isElectrical && (
                <Typography className={classes.condensedViewData}>
                  {componentType || '-'}
                </Typography>
              )}

              <Typography className={classes.condensedViewData}>{remedialAction || '-'}</Typography>
              {actionsList.some((action: deficiencyAction) => action.actionId) &&
                actionsList
                  .filter(action => action.actionId)
                  .map((action: deficiencyAction) => {
                    return (
                      <React.Fragment key={action.actionId}>
                        <Typography display="inline" className={classes.condensedViewData}>
                          {action.actionType}
                        </Typography>
                        <span className={classes.greenDot} />
                        <Typography display="inline" className={classes.condensedViewData}>
                          {action.actionId}
                        </Typography>
                        <br />
                      </React.Fragment>
                    );
                  })}

              {isElectrical && (
                <Typography className={classes.condensedViewData}>
                  {repairFactorId ? repairFactor(repairFactorId) : '-'}
                </Typography>
              )}
            </Grid>

            {deficiencyActionedList && (
              <Grid item xs={1}>
                <StatusLabel
                  taskStatusId={deficiencyActionedList.componentFailureStatus}
                  workPackageId={deficiencyActionedList.workPackageId}
                  customClass="deficiencyStatus"
                  label={getStatusForDeficiency(
                    deficiencyActionedList.componentFailureStatus || 0,
                    deficiencyActionedList.workPackageId,
                  )}
                />
              </Grid>
            )}

            <Grid
              item
              xs={3}
              container
              justifyContent="flex-end"
              alignItems="center"
              className={classes.condensedViewPhotoGrid}
            >
              {deficiencyFiles?.length > 0 && (
                <Grid
                  onClick={setFile}
                  item
                  style={{
                    backgroundImage: !fileLoading ? `url("${imageURL}")` : 'inherit',
                  }}
                  id={`bg-condensedView-${componentFailureId}`}
                  className={`${classes.condensedViewImage} ${classes.bgImage}`}
                >
                  {!fileLoading ? (
                    <div
                      className={deficiencyFiles?.length > 1 ? classes.cardViewPhotoBackground : ''}
                    >
                      {deficiencyFiles?.length > 1 && (
                        <div className={classes.photoDivCondensedView}>
                          <Typography
                            className={classes.condensedViewImageText}
                            style={{
                              fontSize: 16,
                            }}
                          >
                            {deficiencyFiles?.length - 1}
                          </Typography>
                          <Typography
                            className={classes.condensedViewImageText}
                            style={{
                              fontSize: 14,
                            }}
                          >
                            More
                          </Typography>
                        </div>
                      )}
                    </div>
                  ) : (
                    <CircularProgress className={classes.loader} />
                  )}
                </Grid>
              )}
              {isEditable && !deficiencyActionedList && (
                <Grid
                  data-testid={`copy-def-${pkDeficiencyUUID}`}
                  item
                  className={classes.condensedViewCopyIcon}
                  onClick={e => {
                    e.stopPropagation();
                    copyDeficiency(componentVersionUUID);
                  }}
                >
                  <Icons.CopyFeather color="primary" />
                </Grid>
              )}
              {componentFailureId !== '0' && (
                <>
                  <Grid
                    data-testid={`audit-def-${pkDeficiencyUUID}`}
                    item
                    onClick={openAuditDialog}
                    className={classes.deleteIcon}
                  >
                    <Icons.OrderHistoryIcon
                      color="primary"
                      aria-label="View"
                      className={classes.customHistory}
                    />
                  </Grid>
                  {isEditable && <Divider orientation="vertical" className={classes.dividerGrid} />}
                </>
              )}

              {isEditable && (
                <>
                  <Grid data-testid={`edit-def-${pkDeficiencyUUID}`} item onClick={setFile}>
                    <Icons.EditFeather color="primary" />
                  </Grid>
                  {!deficiencyActionedList && (
                    <Grid
                      data-testid={`del-def-${pkDeficiencyUUID}`}
                      item
                      className={classes.deleteIcon}
                      onClick={e => {
                        setDeleteConfirm(true);
                        e.stopPropagation();
                      }}
                    >
                      <Icons.Trash2Feather color="error" />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {deleteConfirm && (
        <DeleteDeficiency
          open={deleteConfirm}
          onClose={() => setDeleteConfirm(false)}
          componentVersionUUID={componentVersionUUID}
        />
      )}
    </>
  );
};

export default CondensedView;
